<template>
  <transition name="modal-backdrop" appear>
    <div v-if="isOpen" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <transition name="modal" appear>
        <div v-if="isOpen" class="bg-white border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] rounded-lg p-6 w-full max-w-md mx-4 modal-content">
      <!-- Modal Header -->
      <div class="flex items-center justify-center mb-4">
        <div class="w-16 h-16 rounded-full flex items-center justify-center" :class="iconBgClass">
          <component :is="iconComponent" class="w-8 h-8" :class="iconColorClass" />
        </div>
      </div>

      <!-- Modal Content -->
      <div class="text-center mb-6">
        <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ title }}</h3>
        <p class="text-gray-600">{{ message }}</p>
      </div>

      <!-- Session Details (if provided) -->
      <div v-if="sessionDetails" class="bg-gray-50 rounded-lg p-4 mb-6">
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="font-medium text-gray-700">Student:</span>
            <span class="text-gray-900">{{ sessionDetails.studentName }}</span>
          </div>
          <div class="flex justify-between">
            <span class="font-medium text-gray-700">Subject:</span>
            <span class="text-gray-900">{{ sessionDetails.subject }}</span>
          </div>
          <div class="flex justify-between">
            <span class="font-medium text-gray-700">Date:</span>
            <span class="text-gray-900">{{ sessionDetails.date }}</span>
          </div>
          <div class="flex justify-between">
            <span class="font-medium text-gray-700">Time:</span>
            <span class="text-gray-900">{{ sessionDetails.time }}</span>
          </div>
          <div class="flex justify-between">
            <span class="font-medium text-gray-700">Type:</span>
            <span class="text-gray-900">{{ sessionDetails.type === 'face-to-face' ? 'Face-to-Face' : 'Online' }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3">
        <button 
          @click="handleCancel"
          :disabled="isLoading"
          class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          Cancel
        </button>
        <button 
          @click="handleConfirm"
          :disabled="isLoading"
          class="flex-1 px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :class="confirmButtonClass"
        >
          <span v-if="isLoading" class="flex items-center justify-center gap-2">
            <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ isLoading ? 'Processing...' : confirmText }}
          </span>
          <span v-else>{{ confirmText }}</span>
        </button>
      </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline'

interface SessionDetails {
  studentName: string
  subject: string
  date: string
  time: string
  type: 'face-to-face' | 'online'
}

interface Props {
  isOpen: boolean
  type: 'accept' | 'decline'
  title: string
  message: string
  sessionDetails?: SessionDetails
  isLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false
})

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

// Computed properties for styling
const iconComponent = computed(() => {
  return props.type === 'accept' ? CheckCircleIcon : XCircleIcon
})

const iconBgClass = computed(() => {
  return props.type === 'accept' 
    ? 'bg-green-100' 
    : 'bg-red-100'
})

const iconColorClass = computed(() => {
  return props.type === 'accept' 
    ? 'text-green-600' 
    : 'text-red-600'
})

const confirmText = computed(() => {
  return props.type === 'accept' ? 'Accept' : 'Decline'
})

const confirmButtonClass = computed(() => {
  return props.type === 'accept'
    ? 'bg-green-600 text-white hover:bg-green-700'
    : 'bg-red-600 text-white hover:bg-red-700'
})

// Event handlers
const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('cancel')
}
</script>

<style scoped>
/* Modal backdrop fade transition */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

/* Modal content animations */
.modal-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-leave-active {
  transition: all 0.25s ease-in;
}

.modal-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

.modal-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

.modal-content {
  animation: modalSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalSlideIn {
  0% {
    transform: scale(0.8) translateY(-30px);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}
</style>
